import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiUsers, FiTrendingUp, FiShield, FiCheckCircle, FiBook, FiExternalLink } = FiIcons;

const About = () => {
  const researchFindings = [
    {
      icon: FiTarget,
      title: 'Performance Impact',
      description: 'Teams with high psychological safety are 67% more likely to avoid failures and 47% more likely to exceed performance goals.',
      source: 'Google Project Aristotle Study'
    },
    {
      icon: FiUsers,
      title: 'Team Dynamics',
      description: 'Psychological safety was the most important factor distinguishing high-performing teams from average ones.',
      source: 'Harvard Business School Research'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation Driver',
      description: 'Teams with psychological safety are 76% more likely to engage in innovative behaviors and creative problem-solving.',
      source: 'MIT Sloan Management Review'
    },
    {
      icon: FiShield,
      title: 'Risk Taking',
      description: 'Psychologically safe environments encourage calculated risk-taking, leading to breakthrough solutions.',
      source: 'Stanford Graduate School of Business'
    }
  ];

  const methodology = [
    {
      step: 1,
      title: 'Research-Based Questions',
      description: 'Our assessment uses validated questions from Google\'s Project Aristotle and academic research to measure the five key dynamics of effective teams.'
    },
    {
      step: 2,
      title: 'Multi-Dimensional Analysis',
      description: 'We evaluate Psychological Safety, Dependability, Structure & Clarity, Meaning, and Impact to provide a comprehensive team health picture.'
    },
    {
      step: 3,
      title: 'Actionable Insights',
      description: 'Results include specific, practical recommendations tailored to your team\'s current state and areas for improvement.'
    },
    {
      step: 4,
      title: 'Continuous Improvement',
      description: 'Track progress over time with regular assessments and measure the impact of implemented changes on team performance.'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <SafeIcon icon={FiBook} className="h-16 w-16 text-blue-600 mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          About Psychological Safety Assessment
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Based on Google's groundbreaking Project Aristotle research, our assessment helps IT consulting companies build high-performing teams through evidence-based psychological safety measurement.
        </p>
      </div>

      {/* Project Aristotle Deep Dive */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Google's Project Aristotle</h2>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">The Research</h3>
            <p className="text-gray-600 mb-6">
              From 2012-2016, Google conducted one of the largest studies on team effectiveness, analyzing 180 teams across the company. The research revealed that team composition (who is on the team) matters far less than team dynamics (how the team works together).
            </p>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Discoveries:</h4>
            <ul className="space-y-3">
              {[
                'Psychological safety is the #1 factor for team success',
                'Skills and experience matter less than team dynamics',
                'The best teams have high psychological safety across all members',
                'Teams can be trained to improve psychological safety'
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">The Five Key Dynamics</h4>
            <div className="space-y-4">
              {[
                { name: 'Psychological Safety', desc: 'Can we show vulnerability on this team?' },
                { name: 'Dependability', desc: 'Can we count on each other to do high quality work on time?' },
                { name: 'Structure & Clarity', desc: 'Are goals, roles, and execution plans clear?' },
                { name: 'Meaning', desc: 'Are we working on something that is personally important?' },
                { name: 'Impact', desc: 'Do we fundamentally believe that the work we\'re doing matters?' }
              ].map((dynamic, index) => (
                <div key={index} className="border-l-4 border-blue-600 pl-4">
                  <h5 className="font-semibold text-gray-900">{index + 1}. {dynamic.name}</h5>
                  <p className="text-sm text-gray-600">{dynamic.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Research Findings */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Research-Backed Benefits</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {researchFindings.map((finding, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <SafeIcon icon={finding.icon} className="h-8 w-8 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">{finding.title}</h3>
              </div>
              <p className="text-gray-600 mb-4">{finding.description}</p>
              <p className="text-sm text-blue-600 font-medium">{finding.source}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Our Methodology */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 mb-16 text-white">
        <h2 className="text-3xl font-bold text-center mb-12">Our Assessment Methodology</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {methodology.map((item, index) => (
            <div key={index} className="text-center">
              <div className="bg-white bg-opacity-20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">{item.step}</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
              <p className="text-blue-100 text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Why IT Teams Need This */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why IT Teams Especially Need Psychological Safety</h2>
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Unique IT Challenges</h3>
            <ul className="space-y-3">
              {[
                'Complex technical problems require collaborative thinking',
                'Rapid technology changes demand continuous learning',
                'Code reviews and technical discussions need open feedback',
                'Project failures can have significant business impact',
                'Remote and distributed teams are common',
                'High-stress environments with tight deadlines'
              ].map((challenge, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                  <span className="text-gray-700">{challenge}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">How Psychological Safety Helps</h3>
            <ul className="space-y-3">
              {[
                'Encourages asking questions about unfamiliar technologies',
                'Enables honest discussions about technical debt and risks',
                'Promotes knowledge sharing and mentoring',
                'Reduces fear of making mistakes while learning',
                'Improves code quality through open peer reviews',
                'Builds trust in distributed team environments'
              ].map((benefit, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="bg-gray-50 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Learn More</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: 'Google\'s Project Aristotle',
              description: 'Read the original research and findings',
              link: 'https://rework.withgoogle.com/intl/en/guides/understanding-team-effectiveness'
            },
            {
              title: 'Amy Edmondson\'s Research',
              description: 'Foundational work on psychological safety',
              link: 'https://www.hbs.edu/faculty/Pages/profile.aspx?facId=6451'
            },
            {
              title: 'Implementation Guide',
              description: 'Practical steps to improve team dynamics',
              link: '#'
            }
          ].map((resource, index) => (
            <div key={index} className="bg-white rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{resource.title}</h3>
              <p className="text-gray-600 mb-4">{resource.description}</p>
              <a 
                href={resource.link} 
                className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 font-medium"
                target="_blank" 
                rel="noopener noreferrer"
              >
                <span>Learn More</span>
                <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default About;